import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../services/api';
import logo from '../assets/logo.png';
import './AccountDeletionRequestPage.css';

const initialForm = {
  fullName: '',
  mobile: '',
  accountId: '',
  reason: '',
};

const AccountDeletionRequestPage = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const canSubmit = useMemo(() => {
    return (
      form.fullName.trim() &&
      /^\d{10,15}$/.test(form.mobile.trim()) &&
      form.accountId.trim()
    );
  }, [form]);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: key === 'mobile' ? value.replace(/\D/g, '').slice(0, 15) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!form.fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^\d{10,15}$/.test(form.mobile.trim())) {
      setError('Please enter a valid mobile number.');
      return;
    }
    if (!form.accountId.trim()) {
      setError('Please enter your account ID.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await API.post('/auth/account-deletion-request', {
        fullName: form.fullName,
        mobile: form.mobile,
        accountId: form.accountId,
        reason: form.reason,
      });
      setMessage(
        data?.message || 'Your request has been submitted to admin for review.'
      );
      setForm(initialForm);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to submit deletion request.'
      );
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/login'));

  return (
    <div className="adr-shell">
      <header className="adr-topbar">
        <div className="adr-topbar-left">
          <button className="adr-back" type="button" onClick={goBack} aria-label="Go back">
            <span className="material-symbols-outlined">arrow_back_ios_new</span>
          </button>
          <Link to="/login" className="adr-brand">
            <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." className="adr-brand-logo" />
            <span>Lotus Rice Mill Pvt. Ltd.</span>
          </Link>
        </div>
        <Link className="adr-login-link" to="/login">Login</Link>
      </header>

      <main className="adr-main">
        <div className="adr-layout">
          <section className="adr-copy">
            <p className="adr-kicker">Public Support Form</p>
            <h1>Request Account Deletion</h1>
            <p>
              If you want your account and associated data to be deleted, submit your
              name, mobile number, and account ID here. Your request will be sent to
              the admin team for review.
            </p>
            <ul className="adr-points">
              <li>No login required</li>
              <li>Admins receive a direct notification</li>
              <li>The deletion process is completed manually after review</li>
            </ul>
          </section>

          <Card className="adr-card shadow-sm border-0">
            <Card.Body className="p-4 p-md-4">
              <motion.h4
                className="mb-1 fw-semibold"
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                Submit deletion request
              </motion.h4>
              <p className="text-muted mb-4">
                Enter your registered details so the admin team can identify your account correctly.
              </p>

              {message ? <Alert variant="success">{message}</Alert> : null}
              {error ? <Alert variant="danger">{error}</Alert> : null}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    value={form.fullName}
                    onChange={(e) => updateField('fullName', e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => updateField('mobile', e.target.value)}
                    placeholder="Enter registered mobile number"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Account ID</Form.Label>
                  <Form.Control
                    value={form.accountId}
                    onChange={(e) => updateField('accountId', e.target.value)}
                    placeholder="Employee ID / Vendor ID / User ID"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Reason (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.reason}
                    onChange={(e) => updateField('reason', e.target.value)}
                    placeholder="Add any note for admin"
                  />
                </Form.Group>

                <Button
                  type="submit"
                  className="w-100 adr-submit-btn"
                  disabled={loading || !canSubmit}
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AccountDeletionRequestPage;
